import { useLocation, Link } from 'react-router-dom';
import { Button, Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx-js-style';
import { getStoredCompanyDetails, getCompanyDisplayFromDetails, getShortCompanyName } from '../../services/companyService';

// Temporary: September 2026 payroll was processed before the mid-month PF rule split existed.
// This page recalculates that month's PF day-wise from stored Gross only to build the ECR file.
// Nothing is saved. Remove this file, its route in App.js and the redirect in ProvidentFundReport.jsx after use.

export const SPLIT_MONTH = { month: 'September', year: '2026', start: '2026-09-01', end: '2026-09-30' };

const SPLIT_RULES = [
    { label: 'Before Ceiling', from: '2026-09-01', to: '2026-09-16', pfLimit: 15000, pfPer: 12, epsLimit: 15000, epsPer: 8.33, edliLimit: 15000, edliPer: 0.5 },
    { label: 'After Ceiling', from: '2026-09-17', to: '2026-09-30', pfLimit: 25000, pfPer: 12, epsLimit: 25000, epsPer: 8.33, edliLimit: 25000, edliPer: 0.5 },
];

const DAY_MS = 86400000;

const toDay = (value) => {
    const text = String(value || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(text) || text.startsWith('0000')) return null;
    return text;
};

const daysBetween = (from, to) => Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS) + 1;

const maxDay = (a, b) => (a > b ? a : b);
const minDay = (a, b) => (a < b ? a : b);

// Same formula as the payroll team's split-month sheet.
function calculateSplitPf(empDetail) {
    const gross = Number(empDetail.Gross) || 0;
    const joining = toDay(empDetail.date_of_joining);
    const leaving = toDay(empDetail.date_of_termination);

    let employedFrom = joining ? maxDay(SPLIT_MONTH.start, joining) : SPLIT_MONTH.start;
    let employedTo = leaving ? minDay(SPLIT_MONTH.end, leaving) : SPLIT_MONTH.end;
    if (employedFrom > employedTo) {
        employedFrom = SPLIT_MONTH.start;
        employedTo = SPLIT_MONTH.end;
    }
    const empDays = daysBetween(employedFrom, employedTo);

    let pfWages = 0, pf = 0, epsWages = 0, eps = 0, edliWages = 0, edli = 0;
    const parts = SPLIT_RULES.map((rule) => {
        const from = maxDay(rule.from, employedFrom);
        const to = minDay(rule.to, employedTo);
        const days = from <= to ? daysBetween(from, to) : 0;

        const grossShare = (gross * days) / empDays;
        const cappedShare = Math.min(grossShare, rule.pfLimit);
        const partPfWages = (cappedShare * days) / empDays;
        const partEpsWages = (Math.min(grossShare, rule.epsLimit) * days) / empDays;
        const partEdliWages = (Math.min(grossShare, rule.edliLimit) * days) / empDays;
        const partPf = (partPfWages * rule.pfPer) / 100;

        pfWages += partPfWages;
        pf += partPf;
        epsWages += partEpsWages;
        eps += (partEpsWages * rule.epsPer) / 100;
        edliWages += partEdliWages;
        edli += (partEdliWages * rule.edliPer) / 100;

        return {
            days,
            grossShare: Math.round(grossShare),
            cap: Math.round(cappedShare),
            wages: Math.round(partPfWages),
            epf: Math.round(partPf),
        };
    });

    const activeParts = parts.filter((part) => part.days > 0);

    return {
        parts,
        Basic_for_PF: Math.round(pfWages),
        EPF: activeParts.length > 1
            ? activeParts.reduce((sum, part) => sum + part.epf, 0)
            : Math.ceil(Number(pf.toFixed(6))),
        EPS: Math.round(Number(eps.toFixed(2))),
        EDLI: Math.round(Number(edli.toFixed(2))),
        EPS_Wages: Math.round(epsWages),
        EDLI_Wages: Math.round(edliWages),
    };
}

const formatNumber = (num) => Math.round(Number(num) || 0).toLocaleString('en-IN');

const cleanDisplayValue = (value) => (value === null || value === undefined ? '' : String(value).replace(/['"]/g, '').trim());

const cellStyle = { textAlign: 'right', padding: '10px 8px', borderBottom: '1px solid #dee2e6' };
const splitCellStyle = { ...cellStyle, backgroundColor: '#fff8e1' };
const headStyle = { ...cellStyle, whiteSpace: 'normal', color: '#fff' };
const summaryLabelStyle = { textAlign: 'left', border: '1px solid #999', padding: '8px', whiteSpace: 'normal', wordWrap: 'break-word' };
const summaryValueStyle = { textAlign: 'right', border: '1px solid #999', padding: '8px' };

function chargeAmount(wages, percent, minAmt) {
    const pct = (Number(percent) || 0) / 100;
    const min = Number(minAmt) || 0;
    const amount = wages * pct;
    return Math.round(amount <= min ? min : amount);
}

function PFSplitECR() {
    const location = useLocation();
    const companyDetails = useSelector((state) => state.auth.activeCompanyDetails) || getStoredCompanyDetails();
    const { companyName, addressLine1, addressLine2 } = getCompanyDisplayFromDetails(companyDetails || {});
    const { empDetails = [], pfDetail = null } = location.state || {};

    const rows = empDetails
        .filter((item) => item?.pf_applicable === 1)
        .map((empDetail) => {
            const pf = calculateSplitPf(empDetail);
            const vpf = Math.round(Number(empDetail.VPFAmount) || 0);
            const name = (empDetail?.full_name && String(empDetail.full_name).trim())
                || `${empDetail?.first_name || ''} ${empDetail?.last_name || ''}`.trim();
            return {
                empDetail,
                code: empDetail.employee_code,
                name: String(name || '').toUpperCase(),
                basic: Math.round(Number(empDetail.Basic) || 0),
                gross: Math.round(Number(empDetail.Gross) || 0),
                totalDays: empDetail.total_days,
                ncp: Number(empDetail.total_days - empDetail.days_worked),
                uan: cleanDisplayValue(empDetail.UANNumber),
                pfNumber: cleanDisplayValue(empDetail.pf_number),
                vpf,
                pf,
            };
        });

    let totalExcludedEmployees = 0;
    let totalExcludedEmployeesGross = 0;
    let totalExcludedEmployeesBasic = 0;
    empDetails.forEach((empDetail) => {
        if (!empDetail.pf_applicable) {
            totalExcludedEmployees += 1;
            totalExcludedEmployeesGross += Math.round(Number(empDetail.Gross) || 0);
            totalExcludedEmployeesBasic += Math.round(Number(empDetail.Basic) || 0);
        }
    });

    const totals = rows.reduce((acc, row) => {
        acc.basic += row.basic;
        acc.gross += row.gross;
        acc.ncp += row.ncp;
        acc.pfWages += row.pf.Basic_for_PF;
        acc.epf += row.pf.EPF;
        acc.vpf += row.vpf;
        acc.eps += row.pf.EPS;
        acc.edli += row.pf.EDLI;
        row.pf.parts.forEach((part, index) => {
            acc.parts[index].grossShare += part.grossShare;
            acc.parts[index].wages += part.wages;
            acc.parts[index].epf += part.epf;
        });
        return acc;
    }, {
        basic: 0, gross: 0, ncp: 0, pfWages: 0, epf: 0, vpf: 0, eps: 0, edli: 0,
        parts: SPLIT_RULES.map(() => ({ grossShare: 0, wages: 0, epf: 0 })),
    });

    const totalEpfEps = totals.epf - totals.eps;
    // Company admin / inspection charges use the month-end (After Ceiling) PF rule values when available.
    const epfAdminCharge = chargeAmount(totals.pfWages, pfDetail?.epfAdminPercent, pfDetail?.epfAdminMinAmt);
    const epfInspectionCharge = chargeAmount(totals.pfWages, pfDetail?.epfInspectionPercent, pfDetail?.epfInspectionMinAmt);
    const edliInspectionCharge = chargeAmount(totals.pfWages, pfDetail?.edliInspectionPercent, pfDetail?.edliInspectionMinAmt);
    const totalAmount = totals.epf + totals.vpf + totalEpfEps + totals.eps + epfAdminCharge + totals.edli + epfInspectionCharge + edliInspectionCharge;

    const colCount = 14 + SPLIT_RULES.length * 4;

    const splitColumnHeaders = SPLIT_RULES.flatMap((rule) => {
        const days = daysBetween(rule.from, rule.to);
        return [
            `Gross Salary ${rule.label}`,
            `EPF Salary Cap ${rule.label}`,
            `EPF Salary ${rule.label} Proportionate (${days} days)`,
            `EPF ${rule.label}`,
        ];
    });

    const handleExcelDownload = () => {
        const wb = XLSX.utils.book_new();
        const wsData = [];
        const headerNames = [
            'Sr No', 'Employee Code', 'Employee Name', 'UAN Number', 'NCP days', 'Gross (INR)',
            ...splitColumnHeaders.map((name) => `${name} (INR)`),
            'EPF Wages (INR)', 'EPF (INR)', 'VPF (INR)', 'EPS (INR)', 'EPF-EPS (INR)', 'EDLI (INR)',
        ];

        const fullDataRows = rows.map((row, index) => ([
            index + 1,
            row.code,
            row.name,
            row.uan,
            row.ncp,
            row.gross,
            ...row.pf.parts.flatMap((part) => [part.grossShare, part.cap, part.wages, part.epf]),
            row.pf.Basic_for_PF,
            row.pf.EPF,
            row.vpf,
            row.pf.EPS,
            row.pf.EPF - row.pf.EPS,
            row.pf.EDLI,
        ]));

        const numCols = headerNames.length;
        wsData.push([companyName || '', ...Array(numCols - 1).fill('')]);
        wsData.push([addressLine1 || '', ...Array(numCols - 1).fill('')]);
        wsData.push([addressLine2 || '', ...Array(numCols - 1).fill('')]);
        wsData.push([`Provident Fund details for ${SPLIT_MONTH.month} ${SPLIT_MONTH.year} (split month)`, ...Array(numCols - 1).fill('')]);
        wsData.push(headerNames);
        fullDataRows.forEach((row) => wsData.push(row));
        wsData.push([
            '', '', 'Total', '', Math.round(totals.ncp), totals.gross,
            ...totals.parts.flatMap((part) => [part.grossShare, '', part.wages, part.epf]),
            totals.pfWages, totals.epf, totals.vpf, totals.eps, totalEpfEps, totals.edli,
        ]);

        wsData.push(Array(numCols).fill(''));
        const summarySectionStartRow = wsData.length;
        wsData.push(['Total number of employees', '', empDetails.length, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total Subscibers', '', rows.length, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total number of employees excluded', '', totalExcludedEmployees, ...Array(numCols - 3).fill('')]);
        wsData.push(['Gross salary of excluded emp', '', totalExcludedEmployeesGross, ...Array(numCols - 3).fill('')]);
        wsData.push(['Gross salary of excluded emp (Basic)', '', totalExcludedEmployeesBasic, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total NCP days', '', Math.round(totals.ncp), ...Array(numCols - 3).fill('')]);
        wsData.push(Array(numCols).fill(''));
        wsData.push(['Summary', '', 'INR', ...Array(numCols - 3).fill('')]);
        wsData.push(['EPF Wages', '', totals.pfWages, ...Array(numCols - 3).fill('')]);
        wsData.push(['EPF Employee', '', totals.epf, ...Array(numCols - 3).fill('')]);
        wsData.push(['VPF Employee', '', totals.vpf, ...Array(numCols - 3).fill('')]);
        wsData.push(['EPF-EPS', '', totalEpfEps, ...Array(numCols - 3).fill('')]);
        wsData.push(['EPS', '', totals.eps, ...Array(numCols - 3).fill('')]);
        wsData.push(['EPF Admin', '', epfAdminCharge, ...Array(numCols - 3).fill('')]);
        wsData.push(['EDLI', '', totals.edli, ...Array(numCols - 3).fill('')]);
        wsData.push(['EPF Insp', '', epfInspectionCharge, ...Array(numCols - 3).fill('')]);
        wsData.push(['EDLI Admin', '', 0, ...Array(numCols - 3).fill('')]);
        wsData.push(['EDLI Insp', '', edliInspectionCharge, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total Challan', '', totalAmount, ...Array(numCols - 3).fill('')]);
        const summarySectionEndRow = wsData.length - 1;
        const summarySpacerRow = summarySectionStartRow - 1;
        const accountSpacerRow = wsData.length;

        const accountSectionStartRow = wsData.length;
        wsData.push(Array(numCols).fill(''));
        wsData.push(['Account Wise Bifurcation of Balance Amount', ...Array(numCols - 1).fill('')]);
        wsData.push(['Total EPF Contribution (A/C 1)', 'Total EPF Contribution EE Share (incl. VPF) + Total EPF Contribution ER Share + Total Refund of Advance (₹)', totals.epf + totals.vpf + totalEpfEps, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total EPF Charges (A/C 2)', 'Administration Charge (₹)', epfAdminCharge, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total EPF Charges (A/C 2)', 'Inspection Charge (₹)', 'Not Applicable', ...Array(numCols - 3).fill('')]);
        wsData.push(['Total EPS Contribution (A/C 10) (₹)', '', totals.eps, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total EDLI Contribution (ER Share A/C 21) (₹)', '', totals.edli, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total EDLI Charges (A/C 22) (₹)', 'Administration Charges (₹)', 0, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total EDLI Charges (A/C 22) (₹)', 'Inspection Charge (₹)', 'Not Applicable', ...Array(numCols - 3).fill('')]);
        wsData.push(['Total Amount (₹)', '', totalAmount, ...Array(numCols - 3).fill('')]);
        const accountHeadingRow = accountSectionStartRow + 1;
        const accountSectionEndRow = wsData.length - 1;

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = headerNames.map((_, col) => ({ wch: col <= 2 ? 22 : 14 }));
        ws['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } },
            { s: { r: 2, c: 0 }, e: { r: 2, c: numCols - 1 } },
            { s: { r: 3, c: 0 }, e: { r: 3, c: numCols - 1 } },
            { s: { r: accountHeadingRow, c: 0 }, e: { r: accountHeadingRow, c: 2 } },
        ];

        const borderStyle = {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } },
        };
        const headerStyle = {
            font: { bold: true, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '5B9BD5' } },
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
            border: borderStyle,
        };
        const companyNameStyle = {
            font: { bold: true, sz: 16, color: { rgb: '000000' } },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: { top: borderStyle.top, bottom: borderStyle.bottom, right: borderStyle.right },
        };

        for (let row = 0; row <= 3; row++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
            if (ws[cellAddress]) ws[cellAddress].s = companyNameStyle;
            for (let col = 1; col < numCols; col++) {
                const emptyCellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[emptyCellAddress]) delete ws[emptyCellAddress];
            }
        }
        for (let col = 0; col < numCols; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 4, c: col });
            if (ws[cellAddress]) ws[cellAddress].s = headerStyle;
        }

        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let row = 5; row <= range.e.r; row++) {
            for (let col = 0; col < numCols; col++) {
                const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (!ws[cellAddress]) continue;
                const cellValue = wsData[row]?.[col];
                const isNumericCell = typeof cellValue === 'number' && !Number.isNaN(cellValue);
                const isMainTotal = row === 5 + rows.length;
                const isAccountRow = row >= accountHeadingRow && row <= accountSectionEndRow;
                ws[cellAddress].s = {
                    border: borderStyle,
                    font: { bold: isMainTotal || (col === 0 && row > 5 + rows.length) || row === accountHeadingRow || row === accountSectionEndRow },
                    alignment: {
                        vertical: 'center',
                        horizontal: isAccountRow ? (col === 2 ? 'right' : 'left') : (isNumericCell ? 'right' : 'left'),
                        wrapText: true,
                    },
                };
                if (isNumericCell) ws[cellAddress].z = '#,##0';
            }
        }

        [summarySpacerRow, accountSpacerRow].forEach((row) => {
            for (let col = 3; col < numCols; col++) {
                const extraCellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[extraCellAddress]) delete ws[extraCellAddress];
            }
        });
        for (let row = summarySectionStartRow; row <= summarySectionEndRow; row++) {
            for (let col = 3; col < numCols; col++) {
                const extraCellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[extraCellAddress]) delete ws[extraCellAddress];
            }
        }
        for (let row = accountHeadingRow; row <= accountSectionEndRow; row++) {
            for (let col = 3; col < numCols; col++) {
                const extraCellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[extraCellAddress]) delete ws[extraCellAddress];
            }
        }

        XLSX.utils.book_append_sheet(wb, ws, 'Provident Fund');
        XLSX.writeFile(wb, `${getShortCompanyName(companyName) || 'Report'} Report for Provident Fund ${SPLIT_MONTH.month.substring(0, 3)} ${SPLIT_MONTH.year}.xlsx`);
    };

    const downloadTxtFile = () => {
        let txtData = rows.map(({ empDetail, name, gross, ncp, vpf, pf }) => {
            const fields = [
                empDetail.UANNumber,
                name,
                gross,
                pf.Basic_for_PF,
                pf.EPS_Wages,
                pf.EDLI_Wages,
                pf.EPF + vpf,
                pf.EPS,
                pf.EPF - pf.EPS,
                ncp,
            ];
            return fields.map((value) => (value ? value : 0)).join('#~#') + '#~#0\n';
        }).join('');
        txtData = txtData.split(',').join('');

        const element = document.createElement('a');
        const file = new Blob([txtData], { type: 'text/plain;charset=utf-8' });
        element.href = URL.createObjectURL(file);
        element.download = `${getShortCompanyName(companyName) || 'Report'}ReportforECR${SPLIT_MONTH.month.substring(0, 3)}${SPLIT_MONTH.year}.txt`;
        element.click();
    };

    if (!location.state) {
        return (
            <Box sx={{ padding: '30px' }}>
                <Typography>Open this page from Reports &gt; Provident Fund Report for September 2026.</Typography>
                <Link to="/reports/provident-fund-report">Go to Provident Fund Report</Link>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '10px', overflowX: 'hidden', maxWidth: '100vw' }}>
            <Box sx={{ width: '100%', maxWidth: '100%', padding: '20px', minWidth: 0, overflow: 'hidden' }}>
                <Box sx={{ backgroundColor: '#212529', color: '#fff', borderRadius: '4px', padding: '12px 16px', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                        Provident Fund (split month) – {SPLIT_MONTH.month} {SPLIT_MONTH.year}
                    </Typography>
                    {SPLIT_RULES.map((rule) => (
                        <Typography key={rule.from} variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                            {rule.label}: {rule.from} to {rule.to} | PF limit ₹{rule.pfLimit.toLocaleString('en-IN')} @ {rule.pfPer}%
                            {' '}| EPS limit ₹{rule.epsLimit.toLocaleString('en-IN')} @ {rule.epsPer}%
                            {' '}| EDLI limit ₹{rule.edliLimit.toLocaleString('en-IN')} @ {rule.edliPer}%
                        </Typography>
                    ))}
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5 }}>
                        PF is recalculated day-wise from each employee&apos;s processed Gross for this report and ECR only. Payroll, salary slips and other reports are not changed.
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    color="success"
                    onClick={handleExcelDownload}
                    sx={{ marginBottom: 2 }}
                >
                    Download Provident Fund Report (excel)
                </Button>
                <Button
                    style={{ marginLeft: '20px', marginBottom: '14px' }}
                    variant="contained"
                    color="secondary"
                    onClick={downloadTxtFile}
                    sx={{ color: 'white' }}
                >
                    Generate ECR
                </Button>

                <div
                    style={{
                        height: '600px',
                        width: '100%',
                        maxWidth: '100%',
                        overflow: 'auto',
                        marginTop: '20px',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        backgroundColor: '#fff',
                    }}
                >
                    <table style={{ borderCollapse: 'collapse', minWidth: '2200px', width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#212529', color: '#fff', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.2)' }}>
                            <tr>
                                <th style={{ ...headStyle, textAlign: 'center' }}>Sr No</th>
                                <th style={{ ...headStyle, textAlign: 'left' }}>Emp Code</th>
                                <th style={{ ...headStyle, textAlign: 'left' }}>Employee Name</th>
                                <th style={{ ...headStyle, textAlign: 'left' }}>UAN</th>
                                <th style={headStyle}>NCP</th>
                                <th style={headStyle}>Gross</th>
                                {SPLIT_RULES.flatMap((rule) => {
                                    const days = daysBetween(rule.from, rule.to);
                                    return [
                                        <th key={`gross-${rule.from}`} style={{ ...headStyle, backgroundColor: '#5c4b00' }}>Gross Salary {rule.label}</th>,
                                        <th key={`cap-${rule.from}`} style={{ ...headStyle, backgroundColor: '#5c4b00' }}>EPF Salary Cap {rule.label}</th>,
                                        <th key={`wages-${rule.from}`} style={{ ...headStyle, backgroundColor: '#5c4b00' }}>EPF Salary {rule.label} Proportionate ({days} days)</th>,
                                        <th key={`epf-${rule.from}`} style={{ ...headStyle, backgroundColor: '#5c4b00' }}>EPF {rule.label}</th>,
                                    ];
                                })}
                                <th style={headStyle}>EPF Wages</th>
                                <th style={headStyle}>EPF</th>
                                <th style={headStyle}>VPF</th>
                                <th style={headStyle}>EPS</th>
                                <th style={headStyle}>EPF-EPS</th>
                                <th style={headStyle}>EDLI</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr key={row.code ?? index}>
                                    <td style={{ ...cellStyle, textAlign: 'center' }}>{index + 1}</td>
                                    <td style={{ ...cellStyle, textAlign: 'left' }}>{row.code}</td>
                                    <td style={{ ...cellStyle, textAlign: 'left' }}>{row.name}</td>
                                    <td style={{ ...cellStyle, textAlign: 'left' }}>{row.uan}</td>
                                    <td style={cellStyle}>{row.ncp}</td>
                                    <td style={cellStyle}>{formatNumber(row.gross)}</td>
                                    {row.pf.parts.flatMap((part, partIndex) => [
                                        <td key={`gross-${partIndex}`} style={splitCellStyle}>{formatNumber(part.grossShare)}</td>,
                                        <td key={`cap-${partIndex}`} style={splitCellStyle}>{formatNumber(part.cap)}</td>,
                                        <td key={`wages-${partIndex}`} style={splitCellStyle}>{formatNumber(part.wages)}</td>,
                                        <td key={`epf-${partIndex}`} style={splitCellStyle}>{formatNumber(part.epf)}</td>,
                                    ])}
                                    <td style={cellStyle}>{formatNumber(row.pf.Basic_for_PF)}</td>
                                    <td style={cellStyle}>{formatNumber(row.pf.EPF)}</td>
                                    <td style={cellStyle}>{formatNumber(row.vpf)}</td>
                                    <td style={cellStyle}>{formatNumber(row.pf.EPS)}</td>
                                    <td style={cellStyle}>{formatNumber(row.pf.EPF - row.pf.EPS)}</td>
                                    <td style={cellStyle}>{formatNumber(row.pf.EDLI)}</td>
                                </tr>
                            ))}
                            <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                                <td style={cellStyle} />
                                <td style={cellStyle} />
                                <td style={{ ...cellStyle, textAlign: 'left' }}>GRAND TOTAL</td>
                                <td style={cellStyle} />
                                <td style={cellStyle}>{Math.round(totals.ncp)}</td>
                                <td style={cellStyle}>{formatNumber(totals.gross)}</td>
                                {totals.parts.flatMap((part, partIndex) => [
                                    <td key={`gross-${partIndex}`} style={splitCellStyle}>{formatNumber(part.grossShare)}</td>,
                                    <td key={`cap-${partIndex}`} style={splitCellStyle} />,
                                    <td key={`wages-${partIndex}`} style={splitCellStyle}>{formatNumber(part.wages)}</td>,
                                    <td key={`epf-${partIndex}`} style={splitCellStyle}>{formatNumber(part.epf)}</td>,
                                ])}
                                <td style={cellStyle}>{formatNumber(totals.pfWages)}</td>
                                <td style={cellStyle}>{formatNumber(totals.epf)}</td>
                                <td style={cellStyle}>{formatNumber(totals.vpf)}</td>
                                <td style={cellStyle}>{formatNumber(totals.eps)}</td>
                                <td style={cellStyle}>{formatNumber(totalEpfEps)}</td>
                                <td style={cellStyle}>{formatNumber(totals.edli)}</td>
                            </tr>

                            <tr>
                                <td colSpan={2} style={{ textAlign: 'left', border: '1px solid #999', padding: '8px' }}>&nbsp;</td>
                                <td colSpan={2} style={{ textAlign: 'left', border: '1px solid #999', padding: '8px' }}> </td>
                            </tr>

                            <tr>
                                <th colSpan={2} style={summaryLabelStyle}>Total number of employees</th>
                                <td colSpan={2} style={summaryValueStyle}>{empDetails.length}</td>
                            </tr>
                            <tr>
                                <th colSpan={2} style={summaryLabelStyle}>Total Subscibers</th>
                                <td colSpan={2} style={summaryValueStyle}>{rows.length}</td>
                            </tr>
                            <tr>
                                <th colSpan={2} style={summaryLabelStyle}>Total number of employees excluded</th>
                                <td colSpan={2} style={summaryValueStyle}>{totalExcludedEmployees}</td>
                            </tr>
                            <tr>
                                <th colSpan={2} style={summaryLabelStyle}>Gross salary of excluded emp</th>
                                <td colSpan={2} style={summaryValueStyle}>{formatNumber(totalExcludedEmployeesGross)}</td>
                            </tr>
                            <tr>
                                <th colSpan={2} style={summaryLabelStyle}>Basic salary of the excluded employee</th>
                                <td colSpan={2} style={summaryValueStyle}>{formatNumber(totalExcludedEmployeesBasic)}</td>
                            </tr>
                            <tr>
                                <th colSpan={2} style={summaryLabelStyle}>Total NCP days</th>
                                <td colSpan={2} style={summaryValueStyle}>{Math.round(totals.ncp)}</td>
                            </tr>

                            <tr>
                                <td colSpan={colCount} style={{ textAlign: 'left', border: '1px solid #999', padding: '8px' }}>&nbsp;</td>
                            </tr>

                            <tr>
                                <th colSpan={colCount} style={{ textAlign: 'center', border: '1px solid #999', padding: '12px', fontWeight: 'bold', fontSize: '16px', backgroundColor: '#f0f0f0' }}>
                                    Account Wise Bifurcation of Balance Amount
                                </th>
                            </tr>
                            <tr>
                                <th colSpan={5} style={summaryLabelStyle}>Total EPF Contribution (A/C 1)</th>
                                <td colSpan={colCount - 8} style={{ ...summaryLabelStyle, textAlign: 'left' }}>
                                    Total EPF Contribution EE Share (incl. VPF) + Total EPF Contribution ER Share + Total Refund of Advance (₹)
                                </td>
                                <td colSpan={3} style={{ ...summaryValueStyle, fontWeight: 'bold' }}>
                                    {formatNumber(totals.epf + totals.vpf + totalEpfEps)}
                                </td>
                            </tr>
                            <tr>
                                <th rowSpan={2} colSpan={5} style={{ ...summaryLabelStyle, verticalAlign: 'top' }}>Total EPF Charges (A/C 2)</th>
                                <td colSpan={colCount - 8} style={summaryLabelStyle}>Administration Charge (₹)</td>
                                <td colSpan={3} style={summaryValueStyle}>{formatNumber(epfAdminCharge)}</td>
                            </tr>
                            <tr>
                                <td colSpan={colCount - 8} style={summaryLabelStyle}>Inspection Charge (₹)</td>
                                <td colSpan={3} style={summaryValueStyle}>Not Applicable</td>
                            </tr>
                            <tr>
                                <th colSpan={5} style={summaryLabelStyle}>Total EPS Contribution (A/C 10) (₹)</th>
                                <td colSpan={colCount - 8} style={summaryLabelStyle}>&nbsp;</td>
                                <td colSpan={3} style={{ ...summaryValueStyle, fontWeight: 'bold' }}>{formatNumber(totals.eps)}</td>
                            </tr>
                            <tr>
                                <th colSpan={5} style={summaryLabelStyle}>Total EDLI Contribution (ER Share A/C 21) (₹)</th>
                                <td colSpan={colCount - 8} style={summaryLabelStyle}>&nbsp;</td>
                                <td colSpan={3} style={{ ...summaryValueStyle, fontWeight: 'bold' }}>{formatNumber(totals.edli)}</td>
                            </tr>
                            <tr>
                                <th rowSpan={2} colSpan={5} style={{ ...summaryLabelStyle, verticalAlign: 'top' }}>Total EDLI Charges (A/C 22) (₹)</th>
                                <td colSpan={colCount - 8} style={summaryLabelStyle}>Administration Charges (₹)</td>
                                <td colSpan={3} style={summaryValueStyle}>0</td>
                            </tr>
                            <tr>
                                <td colSpan={colCount - 8} style={summaryLabelStyle}>Inspection Charge (₹)</td>
                                <td colSpan={3} style={summaryValueStyle}>Not Applicable</td>
                            </tr>
                            <tr>
                                <th colSpan={colCount - 3} style={{ textAlign: 'right', border: '1px solid #999', padding: '12px', fontWeight: 'bold', backgroundColor: '#d4edda', fontSize: '14px' }}>
                                    Total Amount (₹)
                                </th>
                                <th colSpan={3} style={{ textAlign: 'right', border: '1px solid #999', padding: '12px', fontWeight: 'bold', backgroundColor: '#d4edda', fontSize: '14px' }}>
                                    {formatNumber(totalAmount)}
                                </th>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </Box>
        </Box>
    );
}

export default PFSplitECR;
